import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { useAuth } from './useAuth';
import {
  fetchUserCompetitions,
  fetchCompetitionStats,
  fetchFriendCompetitions,
  addCompetition,
  updateCompetition,
  deleteCompetition,
  clearCompetitions,
  clearFriendCompetitions,
  clearAllFriendCompetitions,
  resetError,
  selectCompetitions,
  selectCompetitionIds,
  selectCompetitionStats,
  selectFriendsCompetitions,
  selectCompetitionLoading,
  selectCompetitionError,
  selectMedalCounts,
  selectWinLossRecord
} from '../store/slices/competitionSlice';
import { Competition, CompetitionType } from '../types/competition';
import logger from '../utils/logger';

// Define a flexible type that allows for any competition properties
type FlexibleCompetition = Competition & {
  [key: string]: any;
};

/**
 * Custom hook for accessing and manipulating competition data
 */
export const useCompetition = () => {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  
  // Select state from Redux store
  const competitions = useAppSelector(selectCompetitions);
  const competitionIds = useAppSelector(selectCompetitionIds);
  const stats = useAppSelector(selectCompetitionStats);
  const friendsCompetitions = useAppSelector(selectFriendsCompetitions);
  const isLoading = useAppSelector(selectCompetitionLoading);
  const error = useAppSelector(selectCompetitionError);
  const medalCounts = useAppSelector(selectMedalCounts);
  const winLossRecord = useAppSelector(selectWinLossRecord);
  
  // Convert competitions object to sorted array (newest first)
  const competitionsArray = competitionIds
    .map(id => competitions[id] as unknown as FlexibleCompetition)
    .sort((a, b) => b.date - a.date);
  
  // Action creators
  const loadUserCompetitions = useCallback((userId: string) => {
    return dispatch(fetchUserCompetitions(userId)).unwrap();
  }, [dispatch]);
  
  const loadCompetitionStats = useCallback((userId: string) => {
    return dispatch(fetchCompetitionStats(userId)).unwrap();
  }, [dispatch]);
  
  const loadFriendCompetitions = useCallback((userId: string) => {
    return dispatch(fetchFriendCompetitions(userId)).unwrap();
  }, [dispatch]);
  
  const createCompetition = useCallback((competitionData: Omit<Competition, 'id' | 'createdAt' | 'updatedAt'>) => {
    return dispatch(addCompetition(competitionData)).unwrap();
  }, [dispatch]);
  
  const updateCompetitionData = useCallback((id: string, data: Partial<Competition>) => {
    return dispatch(updateCompetition({ id, data })).unwrap();
  }, [dispatch]);
  
  const removeCompetition = useCallback((id: string) => {
    return dispatch(deleteCompetition(id)).unwrap();
  }, [dispatch]);
  
  const clearUserCompetitions = useCallback(() => {
    dispatch(clearCompetitions());
  }, [dispatch]);
  
  const clearFriendCompetitionsData = useCallback((userId: string) => {
    dispatch(clearFriendCompetitions(userId));
  }, [dispatch]);
  
  const clearAllFriendCompetitionsData = useCallback(() => {
    dispatch(clearAllFriendCompetitions());
  }, [dispatch]);
  
  const clearError = useCallback(() => {
    dispatch(resetError());
  }, [dispatch]);
  
  // Helper functions
  const getCompetitionsByType = useCallback((type: CompetitionType) => {
    return competitionsArray.filter(competition => competition.type === type);
  }, [competitionsArray]);
  
  const getCompetitionsByYear = useCallback(() => {
    const result: Record<string, FlexibleCompetition[]> = {};
    
    competitionsArray.forEach(comp => {
      const year = new Date(comp.date).getFullYear().toString();
      
      if (!result[year]) {
        result[year] = [];
      }
      
      result[year].push(comp);
    });
    
    return result;
  }, [competitionsArray]);
  
  const getMedalCount = useCallback(() => {
    return medalCounts;
  }, [medalCounts]);
  
  const getWinLossRecord = useCallback(() => {
    return winLossRecord;
  }, [winLossRecord]);
  
  // Actual implementation for match management
  const addMatch = useCallback(async (competitionId: string, matchData: any) => {
    try {
      // Import dynamically to avoid circular imports
      const { addMatch } = await import('../services/firebase/competition.service');
      return await addMatch(competitionId, matchData);
    } catch (error) {
      logger.error('Error adding match:', error);
      throw error;
    }
  }, []);

  const getMatch = useCallback(async (competitionId: string, matchId: string) => {
    try {
      // Import dynamically to avoid circular imports
      const { getMatch } = await import('../services/firebase/competition.service');
      return await getMatch(competitionId, matchId);
    } catch (error) {
      logger.error('Error getting match:', error);
      throw error;
    }
  }, []);

  const updateMatch = useCallback(async (competitionId: string, matchId: string, matchData: any) => {
    try {
      // Import dynamically to avoid circular imports
      const { updateMatch } = await import('../services/firebase/competition.service');
      return await updateMatch(competitionId, matchId, matchData);
    } catch (error) {
      logger.error('Error updating match:', error);
      throw error;
    }
  }, []);

  const deleteMatch = useCallback(async (competitionId: string, matchId: string) => {
    try {
      // Import dynamically to avoid circular imports
      const { deleteMatch } = await import('../services/firebase/competition.service');
      return await deleteMatch(competitionId, matchId);
    } catch (error) {
      logger.error('Error deleting match:', error);
      throw error;
    }
  }, []);

  const getCompetitionMatches = useCallback(async (competitionId: string) => {
    try {
      // Import dynamically to avoid circular imports
      const { getCompetitionMatches } = await import('../services/firebase/competition.service');
      return await getCompetitionMatches(competitionId);
    } catch (error) {
      logger.error('Error getting competition matches:', error);
      throw error;
    }
  }, []);

  const getUserMatches = useCallback(async (userId?: string) => {
    try {
      // Use current user if no userId provided
      const targetUserId = userId || user?.uid;
      if (!targetUserId) {
        throw new Error('User ID required');
      }
      
      // Import dynamically to avoid circular imports
      const { getUserMatches } = await import('../services/firebase/competition.service');
      return await getUserMatches(targetUserId);
    } catch (error) {
      logger.error('Error getting user matches:', error);
      throw error;
    }
  }, [user]);

  // Temporary function for compatibility with EditMatchPage.tsx
  const getCompetition = useCallback((id: string) => {
    return competitions[id] || null;
  }, [competitions]);

  return {
    // State
    competitions: competitionsArray,
    stats,
    friendsCompetitions,
    isLoading,
    error,
    medalCounts,
    winLossRecord,
    
    // Actions
    loadUserCompetitions,
    loadCompetitionStats,
    loadFriendCompetitions,
    createCompetition,
    updateCompetition: updateCompetitionData,
    deleteCompetition: removeCompetition,
    clearUserCompetitions,
    clearFriendCompetitionsData,
    clearAllFriendCompetitionsData,
    clearError,
    
    // Match management
    addMatch,
    getMatch,
    updateMatch,
    deleteMatch,
    getCompetitionMatches,
    getUserMatches,
    
    // Compatibility
    getCompetition,
    
    // Helpers
    getCompetitionsByType,
    getCompetitionsByYear,
    getMedalCount,
    getWinLossRecord
  };
};

export default useCompetition;